import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { Dispatch, JSX, SetStateAction } from "react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes } from "react-router";
import * as z from "zod/v4";
import { AppLayout } from "./components/AppLayout.tsx";
import { AuthProvider } from "./components/auth.tsx";
import AuthenticationForm from "./components/AuthenticationForm.tsx";
import { AuthenticationStatus } from "./components/AuthenticationStatus.tsx";
import Authenticator, { getCachedAccessToken } from "./components/Authenticator.tsx";
import MealListing from "./components/meals/MealListing.tsx";
import { ServerHealthChecks } from "./components/pages/ServerHealthChecks.tsx";
import { TaskDocumentsPage } from "./components/pages/TaskDocumentsPage.tsx";
import { ORPCReactProvider, useORPC } from "./orpc-react.tsx";
import { ApplicationMode, Configuration } from "./schema.ts";
import { ImportArgs } from "./schema/import-meals.ts";

const Accounting = lazy(() => import("./routes/accounting/Index.tsx"));

const queryClient = new QueryClient();

const ApplicationProps = Configuration.extend({
  apiURL: Configuration.shape.apiURL.unwrap(),
  mode: Configuration.shape.mode.unwrap(),
});
type ApplicationProps = z.input<typeof ApplicationProps>;

function useConfigFromPropsOrDefaults(
  givenProps: z.output<typeof ApplicationProps>,
): [effective: Configuration, setConfig: Dispatch<SetStateAction<Configuration | undefined>>] {
  const [localConfig, setLocalConfig] = useState<ApplicationProps | undefined>(givenProps);

  const apiURL = givenProps.apiURL ?? localConfig?.apiURL ?? Configuration.defaults.apiURL;
  const mode = givenProps.mode ?? localConfig?.mode ?? Configuration.defaults.mode;

  const effectiveConfig: Configuration = useMemo(
    () => ({ apiURL, mode }),
    [apiURL instanceof URL ? apiURL.href : apiURL, mode],
  );

  useEffect(() => {
    if (effectiveConfig.mode === ApplicationMode.enum.Development)
      console.debug(`Application configuration is now `, effectiveConfig);
  }, [effectiveConfig]);

  useEffect(() => {
    if (mode === "development") (globalThis as Record<string, unknown>).setConfig = setLocalConfig;
  }, [mode, setLocalConfig]);

  return [effectiveConfig, setLocalConfig as any];
}

export function Application(props: ApplicationProps): JSX.Element {
  /**
   * Since the configuration in props to this component is a kind of application
   * boundary, we use additional runtime checks to ensure the provided
   * configuration is valid and leverages default values everywhere else.
   */
  const givenConfig = ApplicationProps.parse(props);
  const [config, setConfig] = useConfigFromPropsOrDefaults(givenConfig);

  return (
    <QueryClientProvider client={queryClient}>
      {config.mode === "development" && <ReactQueryDevtools />}
      <ORPCReactProvider accessToken={getCachedAccessToken} apiURL={config.apiURL}>
        <AuthProvider>
          <Routes>
            <Route
              element={
                <Suspense>
                  <Authenticator>
                    <AppLayout
                      topRight={
                        <>
                          <AuthenticationStatus />
                        </>
                      }
                    />
                  </Authenticator>
                </Suspense>
              }
            >
              <Route
                index
                element={
                  <>
                    Home
                    <br />
                    <AuthenticationForm />
                  </>
                }
              />
              <Route
                path="/accounting/*"
                element={
                  <Suspense fallback={<div aria-busy="true">Loading accounting...</div>}>
                    <Accounting />
                  </Suspense>
                }
              />
              <Route
                path="/meals"
                element={
                  <Meals
                    range="Meals!A2:G"
                    sheetId="1jpgP1mhp4fYov_8PARwBMPsSJU8rwsR2npWydg3Ykt4"
                  />
                }
              />
              <Route path="/services" element={<ServerHealthChecks />} />
              <Route path="/tasks" element={<TaskDocumentsPage />} />
              <Route path="/*" element={<Navigate to="/" />} />
            </Route>
          </Routes>
        </AuthProvider>
      </ORPCReactProvider>
    </QueryClientProvider>
  );
}

const recipeURL = `https://minimalistbaker.com/roasted-squash-salad-with-crispy-shallot-balsamic-reduction`;

function Meals(props: ImportArgs): JSX.Element {
  const orpc = useORPC();
  const mealsQuery = useQuery(orpc.importMeals.queryOptions({ input: props }));

  const recipeQuery = useQuery(orpc.recipe.queryOptions({ input: { recipeURL } }));
  useEffect(() => {
    if (recipeQuery.error) console.warn(recipeQuery.error);
    if (recipeQuery.data) console.info(`${recipeURL} : `, recipeQuery.data);
  }, [recipeQuery.data, recipeQuery.error]);

  switch (true) {
    case mealsQuery.isError:
      return <>{mealsQuery.error.message}</>;
    case mealsQuery.isLoading:
      return <>Loading...</>;
    case !mealsQuery.data:
      return <>No meals loaded and no error</>;
    default:
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "1em" }}>
          <h1>Meals</h1>
          {mealsQuery.data.errors && mealsQuery.data.errors.length > 0 && (
            <>
              {mealsQuery.data.errors.map((error, i) => (
                <div key={`${i}-${error}`}>{error}</div>
              ))}
            </>
          )}
          <MealListing meals={mealsQuery.data.meals} />
        </div>
      );
  }
}
